import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiPlus, FiEdit, FiTrash2, FiEye, FiFilter, FiSearch,
  FiBook, FiClock, FiUsers, FiStar, FiImage 
} from 'react-icons/fi';
import { 
  getRecipesAdmin, createRecipe, updateRecipe, deleteRecipe 
} from '../../utils/adminApi';
import ImageUpload from '../../components/admin/ImageUpload';

const AdminRecipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [filters, setFilters] = useState({
    meal_type: '',
    goal_type: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0
  });

  const [recipeForm, setRecipeForm] = useState({
    title: '',
    description: '',
    ingredients: '',
    instructions: '',
    meal_type: 'breakfast',
    prep_time: '',
    cook_time: '',
    servings: '',
    calories_per_serving: '',
    goal_type: 'both',
    is_featured: false,
    image_url: '',
    youtube_video_id: ''
  });
  
  const [recipeImageUrl, setRecipeImageUrl] = useState('');

  // Image upload handler
  const handleRecipeImageUpload = (data) => {
    if (data.image_url) {
      setRecipeImageUrl(data.image_url);
      setRecipeForm(prev => ({ ...prev, image_url: data.image_url }));
    }
  };

  const handleRecipeImageRemove = () => {
    setRecipeImageUrl('');
    setRecipeForm(prev => ({ ...prev, image_url: '' }));
  };

  useEffect(() => {
    fetchRecipes();
  }, [filters, pagination.page]);

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      const data = await getRecipesAdmin({
        page: pagination.page,
        per_page: 10,
        ...filters
      });
      setRecipes(data.recipes);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to fetch recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Split ingredients string by newlines, trim, and filter out empty lines
      const ingredientsArray = typeof recipeForm.ingredients === 'string'
        ? recipeForm.ingredients.split('\n').map(ing => ing.trim()).filter(Boolean)
        : [];

      const formData = {
        ...recipeForm,
        ingredients: ingredientsArray.length > 0 ? ingredientsArray : [''],
        instructions: recipeForm.instructions.trim() || '',
        prep_time: parseInt(recipeForm.prep_time) || null,
        cook_time: parseInt(recipeForm.cook_time) || null,
        servings: parseInt(recipeForm.servings) || null,
        calories_per_serving: parseInt(recipeForm.calories_per_serving) || null
      };

      console.log('Sending recipe data:', formData);

      if (editingRecipe) {
        await updateRecipe(editingRecipe.id, formData);
      } else {
        await createRecipe(formData);
      }

      setShowModal(false);
      setEditingRecipe(null);
      resetForm();
      fetchRecipes();
    } catch (error) {
      console.error('Failed to save recipe:', error);
      alert(error.error || 'Failed to save recipe');
    }
  };

  const handleEdit = (recipe) => {
    let ingredientsText = '';
    try {
      const parsed = JSON.parse(recipe.ingredients);
      ingredientsText = Array.isArray(parsed) ? parsed.join('\n') : String(recipe.ingredients);
    } catch {
      ingredientsText = String(recipe.ingredients || '');
    }

    setEditingRecipe(recipe);
    setRecipeForm({
      title: recipe.title,
      description: recipe.description || '',
      ingredients: ingredientsText,
      instructions: recipe.instructions,
      meal_type: recipe.meal_type,
      prep_time: recipe.prep_time || '',
      cook_time: recipe.cook_time || '',
      servings: recipe.servings || '',
      calories_per_serving: recipe.calories_per_serving || '',
      goal_type: recipe.goal_type || 'both',
      is_featured: recipe.is_featured,
      image_url: recipe.image_url || '',
      youtube_video_id: recipe.youtube_video_id || ''
    });
    setRecipeImageUrl(recipe.image_url || '');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this recipe?')) {
      try {
        await deleteRecipe(id);
        fetchRecipes();
      } catch (error) {
        console.error('Failed to delete recipe:', error);
        alert(error.error || 'Failed to delete recipe');
      }
    }
  };

  const resetForm = () => {
    setRecipeForm({
      title: '',
      description: '',
      ingredients: '',
      instructions: '',
      meal_type: 'breakfast',
      prep_time: '',
      cook_time: '',
      servings: '',
      calories_per_serving: '',
      goal_type: 'both',
      is_featured: false,
      image_url: '',
      youtube_video_id: ''
    });
    setRecipeImageUrl('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Recipes Management</h2>
          <p className="text-gray-600">Manage healthy recipes for your clients</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingRecipe(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus className="w-4 h-4" />
          <span>Add Recipe</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="input-field pl-10"
                placeholder="Search recipes..."
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Meal Type</label>
            <select
              value={filters.meal_type}
              onChange={(e) => setFilters(prev => ({ ...prev, meal_type: e.target.value }))}
              className="input-field"
            >
              <option value="">All Types</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="snack">Snack</option>
              <option value="empty_stomach">Empty Stomach</option>
              <option value="bed_time">Bed Time</option>
              <option value="anytime">Anytime a Day</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Goal Type</label>
            <select
              value={filters.goal_type}
              onChange={(e) => setFilters(prev => ({ ...prev, goal_type: e.target.value }))}
              className="input-field"
            >
              <option value="">All Goals</option>
              <option value="weight_loss">Weight Loss</option>
              <option value="weight_gain">Weight Gain</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ meal_type: '', goal_type: '', search: '' })}
              className="btn-secondary w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Recipes Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading recipes...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recipe
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nutrition
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recipes.map((recipe) => (
                    <tr key={recipe.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{recipe.title}</p>
                          <p className="text-sm text-gray-600 line-clamp-2">{recipe.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2 text-sm">
                            <FiBook className="w-4 h-4 text-gray-400" />
                            <span className="capitalize">{recipe.meal_type}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <FiClock className="w-4 h-4 text-gray-400" />
                            <span>{recipe.total_time} min</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <FiUsers className="w-4 h-4 text-gray-400" />
                            <span>{recipe.servings} servings</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p>{recipe.calories_per_serving} cal/serving</p>
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            recipe.goal_type === 'weight_loss' 
                              ? 'bg-blue-100 text-blue-800' 
                              : recipe.goal_type === 'weight_gain'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {recipe.goal_type === 'weight_loss' ? 'Weight Loss' : 
                             recipe.goal_type === 'weight_gain' ? 'Weight Gain' : 'Both'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {recipe.is_featured && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                              <FiStar className="w-3 h-3 mr-1" />
                              Featured
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(recipe)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <FiEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(recipe.id)}
                            className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.pages}
                      className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingRecipe ? 'Edit Recipe' : 'Add New Recipe'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Carousel Display Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  🎬 Featured Video Display (YouTube/Instagram/Facebook)
                </h4>
                <p className="text-xs text-blue-700">
                  The carousel will show: <strong>Video + Title + Prep Time + Meal Type + Goal + Calories</strong>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Ingredients and instructions are optional and not displayed in the homepage carousel.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipe Title *
                  </label>
                  <input
                    type="text"
                    value={recipeForm.title}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, title: e.target.value }))}
                    required
                    className="input-field"
                    placeholder="Enter recipe title"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={recipeForm.description}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="input-field"
                    placeholder="Brief description of the recipe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meal Type *
                  </label>
                  <select
                    value={recipeForm.meal_type}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, meal_type: e.target.value }))}
                    required
                    className="input-field"
                  >
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                    <option value="snack">Snack</option>
                    <option value="empty_stomach">Empty Stomach</option>
                    <option value="bed_time">Bed Time</option>
                    <option value="anytime">Anytime a Day</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Goal Type
                  </label>
                  <select
                    value={recipeForm.goal_type}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, goal_type: e.target.value }))}
                    className="input-field"
                  >
                    <option value="both">Both</option>
                    <option value="weight_loss">Weight Loss</option>
                    <option value="weight_gain">Weight Gain</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prep Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={recipeForm.prep_time}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, prep_time: e.target.value }))}
                    className="input-field"
                    placeholder="15"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cook Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={recipeForm.cook_time}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, cook_time: e.target.value }))}
                    className="input-field"
                    placeholder="30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Servings
                  </label>
                  <input
                    type="number"
                    value={recipeForm.servings}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, servings: e.target.value }))}
                    className="input-field"
                    placeholder="4"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Calories per Serving
                  </label>
                  <input
                    type="number"
                    value={recipeForm.calories_per_serving}
                    onChange={(e) => setRecipeForm(prev => ({ ...prev, calories_per_serving: e.target.value }))}
                    className="input-field"
                    placeholder="320"
                  />
                </div>
              </div>

              {/* Recipe Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiImage className="inline w-4 h-4 mr-1" />
                  Recipe Image
                </label>
                <ImageUpload
                  endpoint="/admin/upload/recipe-image"
                  onUploadSuccess={handleRecipeImageUpload}
                  onUploadError={(error) => console.error('Recipe image upload error:', error)}
                  currentImage={recipeImageUrl}
                  onImageRemove={handleRecipeImageRemove}
                  label="Upload Recipe Image"
                  category="recipes"
                  prefix={recipeForm.title || 'recipe'}
                  className="w-full"
                />
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video Link (YouTube, Instagram, or Facebook Reels)
                </label>
                <input
                  type="text"
                  value={recipeForm.youtube_video_id}
                  onChange={(e) => setRecipeForm(prev => ({ ...prev, youtube_video_id: e.target.value }))}
                  className="input-field"
                  placeholder="e.g. https://youtube.com/shorts/... or https://www.instagram.com/reel/... or https://www.facebook.com/reel/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the full YouTube URL/ID, full Instagram Reel URL, or full Facebook Reel/video URL. Examples:
                  <br />• YouTube URL: https://youtube.com/shorts/dQw4w9WgXcQ
                  <br />• Instagram URL: https://www.instagram.com/reel/C1aB2c3D4e5/
                  <br />• Facebook URL: https://www.facebook.com/reel/1234567890/
                </p>
              </div>

              {/* Ingredients (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ingredients (Optional)
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Enter each ingredient on a new line (use bullets, numbers, or dashes as you prefer).
                </p>
                <textarea
                  value={recipeForm.ingredients}
                  onChange={(e) => setRecipeForm(prev => ({ ...prev, ingredients: e.target.value }))}
                  rows={6}
                  className="input-field"
                  placeholder="e.g.&#10;• 1 cup Rolled Oats&#10;• 1 cup Almond Milk&#10;• 1 tbsp Honey"
                />
              </div>

              {/* Instructions (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instructions (Optional)
                </label>
                <textarea
                  value={recipeForm.instructions}
                  onChange={(e) => setRecipeForm(prev => ({ ...prev, instructions: e.target.value }))}
                  rows={6}
                  className="input-field"
                  placeholder="Step-by-step cooking instructions..."
                />
              </div>

              {/* Featured */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={recipeForm.is_featured}
                  onChange={(e) => setRecipeForm(prev => ({ ...prev, is_featured: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_featured" className="ml-2 text-sm text-gray-700">
                  Mark as featured recipe
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingRecipe(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingRecipe ? 'Update Recipe' : 'Create Recipe'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminRecipes;